<template>
  <div class="nd-page">
    <v-card class="nd-section-card dsd-studio">
      <div class="dsd-studio__top">
        <div>
          <div class="nd-table-toolbar__title">{{ isEditing ? "Editar DSD" : "Novo DSD" }}</div>
          <div class="nd-table-toolbar__subtitle">Planeje o resultado estetico sobre a foto do sorriso do paciente.</div>
        </div>
        <div class="dsd-studio__top-actions">
          <v-btn outlined color="primary" elevation="0" to="/dsd/imagens">
            <v-icon left size="18">mdi-image-multiple-outline</v-icon>
            Biblioteca
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="saving" @click="save">
            <v-icon left size="18">mdi-content-save-outline</v-icon>
            Salvar
          </v-btn>
        </div>
      </div>

      <div class="dsd-studio__body">
        <section class="dsd-canvas-card">
          <div class="dsd-canvas-toolbar">
            <div class="dsd-tool-group">
              <v-file-input
                v-model="form.file"
                class="dsd-hidden-file"
                accept="image/*"
                hide-input
                prepend-icon=""
                ref="photoFile"
               hide-details="auto"/>
              <v-btn small color="primary" elevation="0" @click="pickMainPhoto">
                <v-icon left size="17">mdi-image-plus-outline</v-icon>
                {{ previewImageUrl ? "Trocar foto" : "Imagem do paciente" }}
              </v-btn>
              <v-select
                v-model="form.resourceId"
                :items="assetItems"
                item-text="name"
                item-value="resourceId"
                dense
                outlined hide-details="auto"
                clearable
                class="dsd-main-select"
                label="Usar imagem cadastrada"
              />
            </div>

            <div class="dsd-tool-group">
              <v-btn small :outlined="mode !== 'draw'" color="primary" elevation="0" @click="toggleMode('draw')">
                <v-icon left size="17">mdi-brush-outline</v-icon>
                Desenhar
              </v-btn>
              <v-btn small :outlined="mode !== 'text'" color="primary" elevation="0" @click="toggleMode('text')">
                <v-icon left size="17">mdi-format-text</v-icon>
                Texto
              </v-btn>
              <v-btn small outlined color="primary" elevation="0" @click="clearTransientTools">
                <v-icon left size="17">mdi-eraser</v-icon>
                Limpar
              </v-btn>
            </div>
          </div>

          <div
            ref="stage"
            class="dsd-stage"
            :class="{ 'dsd-stage--empty': !previewImageUrl, 'dsd-stage--draw': mode === 'draw', 'dsd-stage--text': mode === 'text' }"
            @click="handleStageClick"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
          >
            <img
              v-if="previewImageUrl"
              class="dsd-stage__photo"
              :src="previewImageUrl"
              alt="Imagem de trabalho DSD"
              draggable="false"
              @dragstart.prevent
            />
            <div v-else class="dsd-stage__empty">
              <v-icon size="62" color="#a04dc2">mdi-face-woman-shimmer-outline</v-icon>
              <strong>Adicione a foto de trabalho</strong>
              <span>Use uma foto do paciente sorrindo para planejar lentes, clareamento ou outros procedimentos esteticos.</span>
            </div>

            <svg class="dsd-stage__drawing" viewBox="0 0 1000 640" preserveAspectRatio="none">
              <path
                v-for="stroke in strokes"
                :key="stroke.localId"
                :d="stroke.pathData"
                :stroke="stroke.color"
                :stroke-width="stroke.strokeWidth"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                v-if="currentStroke.length"
                :d="pointsToPath(currentStroke)"
                stroke="#a04dc2"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <button
              v-for="overlay in overlays"
              :key="overlay.localId"
              type="button"
              class="dsd-overlay"
              :class="{ 'dsd-overlay--active': selectedLayer && selectedLayer.localId === overlay.localId }"
              :style="overlayStyle(overlay)"
              @mousedown.stop="startDrag($event, overlay)"
              @click.stop="selectLayer(overlay)"
            >
              <img :src="resourceUrl(overlay.resourceId)" :alt="overlay.name" draggable="false" />
            </button>

            <button
              v-for="annotation in annotations"
              :key="annotation.localId"
              type="button"
              class="dsd-annotation"
              :class="{ 'dsd-annotation--active': selectedLayer && selectedLayer.localId === annotation.localId }"
              :style="annotationStyle(annotation)"
              @mousedown.stop="startDrag($event, annotation)"
              @click.stop="selectLayer(annotation)"
            >
              {{ annotation.content }}
            </button>
          </div>

          <div class="dsd-canvas-footer">
            <div>
              <span>Foto principal</span>
              <strong>{{ imageStatus }}</strong>
            </div>
            <div>
              <span>Camadas</span>
              <strong>{{ overlays.length }} apoio(s), {{ annotations.length }} texto(s)</strong>
            </div>
            <div>
              <span>Modo</span>
              <strong>{{ modeLabel }}</strong>
            </div>
          </div>
        </section>

        <aside class="dsd-panel">
          <v-form ref="form" v-model="formValid" class="dsd-panel__form">
            <div class="dsd-panel__section">
              <strong>Dados do planejamento</strong>
              <v-text-field v-model="form.name" label="Nome do DSD" outlined dense :rules="[rules.required]"  hide-details="auto"/>
              <v-autocomplete
                v-model="form.patientId"
                :items="patientItems"
                :search-input.sync="patientSearch"
                item-text="name"
                item-value="id"
                label="Paciente"
                outlined
                dense
                clearable
                hide-no-data
                :rules="[rules.required]"
               hide-details="auto"/>
            </div>

            <div class="dsd-panel__section">
              <div class="dsd-section-title">
                <strong>Imagens de apoio</strong>
                <span>sorrisos, lentes e arcadas</span>
              </div>
              <div class="dsd-asset-grid">
                <button
                  v-for="asset in assetItems"
                  :key="asset.id"
                  type="button"
                  class="dsd-asset-card"
                  @click="addOverlay(asset)"
                >
                  <img :src="resourceUrl(asset.resourceId)" :alt="asset.name" />
                  <span>{{ asset.name }}</span>
                </button>
              </div>
            </div>

            <div class="dsd-panel__section">
              <div class="dsd-section-title">
                <strong>Camada selecionada</strong>
                <span>{{ selectedLayer ? selectedLayer.name || selectedLayer.content : "Nenhuma camada selecionada" }}</span>
              </div>
              <template v-if="selectedLayer">
                <v-slider v-if="selectedLayer.kind === 'overlay'" v-model="selectedLayer.scale" label="Tamanho" min="0.2" max="3" step="0.05" hide-details />
                <v-slider v-if="selectedLayer.kind === 'overlay'" v-model="selectedLayer.opacity" label="Opacidade" min="0.1" max="1" step="0.05" hide-details />
                <v-slider v-if="selectedLayer.kind === 'overlay'" v-model="selectedLayer.rotation" label="Rotação" min="-45" max="45" step="1" hide-details />
                <v-text-field v-if="selectedLayer.kind === 'annotation'" v-model="selectedLayer.content" label="Texto" outlined dense hide-details="auto" />
                <div class="dsd-layer-actions">
                  <v-btn outlined color="error" small elevation="0" @click="removeSelectedLayer">
                    <v-icon left size="16">mdi-delete-outline</v-icon>
                    Remover
                  </v-btn>
                </div>
              </template>
            </div>
          </v-form>

          <div class="nd-form-actions dsd-panel__actions">
            <v-btn outlined color="primary" elevation="0" @click="$router.push('/dsd')">Voltar</v-btn>
            <v-btn color="primary" elevation="0" :loading="saving" @click="save">Salvar</v-btn>
          </div>
        </aside>
      </div>
    </v-card>

    <v-dialog v-model="annotationDialog" max-width="420">
      <v-card class="dsd-dialog">
        <v-card-title>Adicionar texto</v-card-title>
        <v-card-text>
          <v-textarea v-model="annotationDraft.content" label="Observação" outlined rows="3" auto-grow autofocus  hide-details="auto"/>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="annotationDialog = false">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" @click="confirmAnnotation">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";

const stageWidth = 1000;
const stageHeight = 640;

export default {
  name: "DsdDetails",

  data: () => ({
    formValid: false,
    loadingPage: false,
    saving: false,
    patientSearch: "",
    patientSearchTimeout: null,
    localPreviewUrl: null,
    patientItems: [],
    assetItems: [],
    overlays: [],
    annotations: [],
    strokes: [],
    currentStroke: [],
    mode: "select",
    dragging: null,
    annotationDialog: false,
    annotationDraft: { x: 0, y: 0, content: "" },
    selectedLayer: null,
    form: {
      id: null,
      name: "",
      patientId: null,
      resourceId: null,
      date: null,
      file: null,
    },
    rules: {
      required: (value) => !!value || "Campo obrigatório",
    },
  }),

  computed: {
    isEditing() {
      return Boolean(this.form.id);
    },
    imageStatus() {
      return this.previewImageUrl ? "Imagem vinculada" : "Pendente";
    },
    imageUrl() {
      if (!this.form.resourceId) return null;
      return this.resourceUrl(this.form.resourceId);
    },
    previewImageUrl() {
      return this.localPreviewUrl || this.imageUrl;
    },
    modeLabel() {
      if (this.mode === "draw") return "Desenho";
      if (this.mode === "text") return "Texto";
      return "Selecao";
    },
  },

  watch: {
    patientSearch(value) {
      clearTimeout(this.patientSearchTimeout);
      this.patientSearchTimeout = setTimeout(() => this.searchPatients(value), 300);
    },
    "form.file"(file) {
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl);
        this.localPreviewUrl = null;
      }
      if (file) this.localPreviewUrl = URL.createObjectURL(file);
    },
  },

  async created() {
    window.addEventListener("mousemove", this.onDrag);
    window.addEventListener("mouseup", this.stopDrag);
    this.loadingPage = true;
    await Promise.all([this.searchPatients(""), this.loadAssets()]);
    const id = this.$route.params.id;
    if (id) await this.loadDsd(id);
    this.loadingPage = false;
  },

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onDrag);
    window.removeEventListener("mouseup", this.stopDrag);
    if (this.localPreviewUrl) URL.revokeObjectURL(this.localPreviewUrl);
  },

  methods: {
    resourceUrl(resourceId) {
      if (!resourceId) return "";
      const base = axios.defaults.baseURL || "";
      return `${base}/resources/${resourceId}`;
    },
    pickMainPhoto() {
      const input = this.$refs.photoFile && this.$refs.photoFile.$el.querySelector("input[type=file]");
      if (input) input.click();
    },
    async searchPatients(search) {
      try {
        const response = await axios.get("/patients", { params: { start: 0, limit: 20, search } });
        this.patientItems = response.data.data || [];
      } catch {
        this.patientItems = [];
      }
    },
    async loadAssets() {
      try {
        const response = await axios.get("/dsd-assets", { params: { start: 0, limit: 100 } });
        const items = response.data.data || [];
        this.assetItems = items
          .map((item) => ({
            id: item.id,
            name: item.name,
            resourceId: item.resource?.id || item.resourceId || item.resource_id || null,
          }))
          .filter((item) => item.resourceId);
      } catch {
        this.assetItems = [];
      }
    },
    async loadDsd(id) {
      try {
        const response = await axios.get(`/dsd/${id}`);
        const data = response.data.data || {};
        const patient = data.patient || data.patientId || null;
        const patientId = typeof patient === "object" ? patient.id : patient;
        if (typeof patient === "object" && !this.patientItems.some((item) => item.id === patient.id)) {
          this.patientItems.push(patient);
        }
        this.form = {
          id: data.id,
          name: data.name || "",
          patientId: patientId || null,
          resourceId: data.resource?.id || data.resourceId || data.resource_id || null,
          date: data.date || null,
          file: null,
        };
        await this.loadLayers(data.id);
      } catch {
        this.$dialog.notify.error("Erro ao carregar DSD.", { position: "top-right", timeout: 5000 });
      }
    },
    async loadLayers(dsdId) {
      try {
        const [annotations, overlays, drawings] = await Promise.all([
          axios.get(`/dsd/${dsdId}/annotations`),
          axios.get(`/dsd/${dsdId}/overlays`),
          axios.get(`/dsd/${dsdId}/drawings`),
        ]);
        this.annotations = (annotations.data.data || []).map((item) => ({
          kind: "annotation",
          id: item.id,
          localId: `annotation-${item.id}`,
          content: item.content || "",
          x: Number(item.x || item.pos_x || 0),
          y: Number(item.y || item.pos_y || 0),
          color: item.color || "#FFFFFF",
        }));
        this.overlays = (overlays.data.data || []).map((item) => ({
          kind: "overlay",
          id: item.id,
          localId: `overlay-${item.id}`,
          name: item.name || item.resource?.name || "Overlay",
          resourceId: item.resource?.id || item.resource_id || item.resource || null,
          x: Number(item.x || item.pos_x || 120),
          y: Number(item.y || item.pos_y || 120),
          scale: Number(item.scale || 1),
          rotation: Number(item.rotation || 0),
          opacity: Number(item.opacity || 1),
        })).filter((item) => item.resourceId);
        this.strokes = (drawings.data.data || []).map((item) => ({
          kind: "drawing",
          id: item.id,
          localId: `drawing-${item.id}`,
          pathData: item.path_data || item.pathData || "",
          strokeWidth: Number(item.stroke_width || item.strokeWidth || 3),
          color: item.color || "#A04DC2",
        })).filter((item) => item.pathData);
      } catch {
        this.annotations = [];
        this.overlays = [];
        this.strokes = [];
      }
    },
    addOverlay(asset) {
      const overlay = {
        kind: "overlay",
        localId: `overlay-new-${Date.now()}-${Math.random()}`,
        name: asset.name,
        resourceId: asset.resourceId,
        x: 380,
        y: 260,
        scale: 1,
        rotation: 0,
        opacity: 0.9,
      };
      this.overlays.push(overlay);
      this.selectLayer(overlay);
      this.mode = "select";
    },
    selectLayer(layer) {
      this.selectedLayer = layer;
    },
    removeSelectedLayer() {
      if (!this.selectedLayer) return;
      if (this.selectedLayer.kind === "overlay") {
        this.overlays = this.overlays.filter((item) => item.localId !== this.selectedLayer.localId);
      } else if (this.selectedLayer.kind === "annotation") {
        this.annotations = this.annotations.filter((item) => item.localId !== this.selectedLayer.localId);
      }
      this.selectedLayer = null;
    },
    toggleMode(mode) {
      this.mode = this.mode === mode ? "select" : mode;
      this.selectedLayer = null;
    },
    clearTransientTools() {
      if (this.mode === "draw") this.strokes = [];
      if (this.mode === "text") this.annotations = [];
      this.currentStroke = [];
    },
    stagePoint(event) {
      const rect = this.$refs.stage.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(stageWidth, ((event.clientX - rect.left) / rect.width) * stageWidth)),
        y: Math.max(0, Math.min(stageHeight, ((event.clientY - rect.top) / rect.height) * stageHeight)),
      };
    },
    handleStageClick(event) {
      if (this.mode !== "text" || event.target.closest(".dsd-overlay") || event.target.closest(".dsd-annotation")) return;
      const point = this.stagePoint(event);
      this.annotationDraft = { ...point, content: "" };
      this.annotationDialog = true;
    },
    confirmAnnotation() {
      const content = this.annotationDraft.content.trim();
      if (!content) return;
      const annotation = {
        kind: "annotation",
        localId: `annotation-new-${Date.now()}-${Math.random()}`,
        content,
        x: this.annotationDraft.x,
        y: this.annotationDraft.y,
        color: "#FFFFFF",
      };
      this.annotations.push(annotation);
      this.selectLayer(annotation);
      this.annotationDialog = false;
      this.annotationDraft = { x: 0, y: 0, content: "" };
      this.mode = "select";
    },
    startDrawing(event) {
      if (this.mode !== "draw" || event.target.closest(".dsd-overlay") || event.target.closest(".dsd-annotation")) return;
      event.preventDefault();
      this.currentStroke = [this.stagePoint(event)];
    },
    draw(event) {
      if (this.mode !== "draw" || !this.currentStroke.length) return;
      event.preventDefault();
      this.currentStroke.push(this.stagePoint(event));
    },
    stopDrawing() {
      if (!this.currentStroke.length) return;
      this.strokes.push({
        kind: "drawing",
        localId: `drawing-new-${Date.now()}-${Math.random()}`,
        pathData: this.pointsToPath(this.currentStroke),
        strokeWidth: 3,
        color: "#A04DC2",
      });
      this.currentStroke = [];
    },
    pointsToPath(points) {
      return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(" ");
    },
    startDrag(event, layer) {
      this.selectLayer(layer);
      this.dragging = {
        layer,
        startX: event.clientX,
        startY: event.clientY,
        originX: layer.x,
        originY: layer.y,
      };
    },
    onDrag(event) {
      if (!this.dragging) return;
      const rect = this.$refs.stage.getBoundingClientRect();
      const deltaX = ((event.clientX - this.dragging.startX) / rect.width) * stageWidth;
      const deltaY = ((event.clientY - this.dragging.startY) / rect.height) * stageHeight;
      this.dragging.layer.x = Math.max(0, Math.min(stageWidth, this.dragging.originX + deltaX));
      this.dragging.layer.y = Math.max(0, Math.min(stageHeight, this.dragging.originY + deltaY));
    },
    stopDrag() {
      this.dragging = null;
    },
    overlayStyle(overlay) {
      return {
        left: `${(overlay.x / stageWidth) * 100}%`,
        top: `${(overlay.y / stageHeight) * 100}%`,
        opacity: overlay.opacity,
        transform: `translate(-50%, -50%) rotate(${overlay.rotation}deg) scale(${overlay.scale})`,
      };
    },
    annotationStyle(annotation) {
      return {
        left: `${(annotation.x / stageWidth) * 100}%`,
        top: `${(annotation.y / stageHeight) * 100}%`,
        color: annotation.color,
      };
    },
    async uploadResource() {
      if (!this.form.file) return this.form.resourceId || null;
      const formData = new FormData();
      formData.append("file", this.form.file);
      formData.append("name", this.form.file.name);
      const response = await axios.post("/resources", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.data?.id || null;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      if (!this.previewImageUrl) {
        this.$dialog.notify.error("Adicione a imagem de trabalho do paciente.", { position: "top-right", timeout: 5000 });
        return;
      }
      this.saving = true;
      try {
        const resourceId = await this.uploadResource();
        const payload = {
          name: this.form.name,
          patientId: this.form.patientId || null,
          resourceId: resourceId || null,
        };

        let dsdId = this.form.id;
        if (dsdId) {
          await axios.patch(`/dsd/${dsdId}`, payload);
        } else {
          const created = await axios.post("/dsd", payload);
          dsdId = created.data.data?.id;
          this.form.id = dsdId;
        }

        await this.replaceLayers(dsdId);
        this.$dialog.notify.success("DSD salvo com sucesso!", { position: "top-right", timeout: 3000 });
        this.$router.push("/dsd");
      } catch (error) {
        console.error("Erro ao salvar DSD:", error);
        this.$dialog.notify.error("Erro ao salvar DSD.", { position: "top-right", timeout: 5000 });
      } finally {
        this.saving = false;
      }
    },
    async replaceLayers(dsdId) {
      const existing = await Promise.all([
        axios.get(`/dsd/${dsdId}/annotations`),
        axios.get(`/dsd/${dsdId}/overlays`),
        axios.get(`/dsd/${dsdId}/drawings`),
      ]);
      await Promise.all([
        ...(existing[0].data.data || []).map((item) => axios.delete(`/dsd/${dsdId}/annotations/${item.id}`)),
        ...(existing[1].data.data || []).map((item) => axios.delete(`/dsd/${dsdId}/overlays/${item.id}`)),
        ...(existing[2].data.data || []).map((item) => axios.delete(`/dsd/${dsdId}/drawings/${item.id}`)),
      ]);

      await Promise.all([
        ...this.annotations.map((item) => axios.post(`/dsd/${dsdId}/annotations`, {
          type: 1,
          pos_x: item.x,
          pos_y: item.y,
          content: item.content,
          font_size: 14,
          color: item.color || "#FFFFFF",
        })),
        ...this.overlays.map((item) => axios.post(`/dsd/${dsdId}/overlays`, {
          pos_x: item.x,
          pos_y: item.y,
          scale: item.scale,
          rotation: item.rotation,
          opacity: item.opacity,
          resourceId: item.resourceId,
        })),
        ...this.strokes.map((item) => axios.post(`/dsd/${dsdId}/drawings`, {
          path_data: item.pathData,
          stroke_width: item.strokeWidth,
          color: item.color,
        })),
      ]);
    },
  },
};
</script>

<style scoped>
.dsd-studio {
  min-height: calc(100vh - 104px);
}

.dsd-studio__top {
  align-items: center;
  border-bottom: 1px solid #edf0ed;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 18px 22px;
}

.dsd-studio__top-actions,
.dsd-tool-group,
.dsd-layer-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

.dsd-studio__body {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 360px;
  padding: 18px;
}

.dsd-canvas-card,
.dsd-panel {
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  min-width: 0;
}

.dsd-canvas-toolbar {
  align-items: center;
  border-bottom: 1px solid #edf0ed;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px;
}

.dsd-hidden-file {
  display: none;
}

.dsd-main-select {
  max-width: 260px;
}

.dsd-stage {
  aspect-ratio: 1000 / 640;
  background:
    linear-gradient(45deg, rgba(255,255,255,0.035) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255,255,255,0.035) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.035) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.035) 75%),
    #111816;
  background-position: 0 0, 0 12px, 12px -12px, -12px 0;
  background-size: 24px 24px;
  cursor: default;
  overflow: hidden;
  position: relative;
  user-select: none;
}

.dsd-stage--draw {
  cursor: crosshair;
}

.dsd-stage--text {
  cursor: text;
}

.dsd-stage__photo {
  height: 100%;
  left: 0;
  object-fit: contain;
  pointer-events: none;
  position: absolute;
  top: 0;
  user-select: none;
  -webkit-user-drag: none;
  width: 100%;
}

.dsd-stage__empty {
  align-items: center;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 9px;
  height: 100%;
  justify-content: center;
  padding: 28px;
  text-align: center;
}

.dsd-stage__empty strong {
  font-size: 20px;
}

.dsd-stage__empty span {
  color: rgba(255, 255, 255, 0.72);
  max-width: 430px;
}

.dsd-stage__drawing {
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.dsd-overlay,
.dsd-annotation {
  background: transparent;
  border: 1px solid transparent;
  cursor: move;
  padding: 0;
  position: absolute;
  transform-origin: center;
}

.dsd-overlay img {
  display: block;
  max-width: 360px;
  pointer-events: none;
  width: 260px;
}

.dsd-overlay--active,
.dsd-annotation--active {
  border-color: #a04dc2;
  box-shadow: 0 0 0 2px rgba(160, 77, 194, 0.2);
}

.dsd-annotation {
  background: rgba(17, 24, 22, 0.72);
  border-radius: 7px;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  max-width: 260px;
  padding: 7px 10px;
  text-align: left;
  transform: translate(-50%, -50%);
  white-space: pre-wrap;
}

.dsd-canvas-footer {
  border-top: 1px solid #edf0ed;
  display: grid;
  gap: 0;
  grid-template-columns: repeat(3, 1fr);
}

.dsd-canvas-footer div {
  border-right: 1px solid #edf0ed;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 13px 16px;
}

.dsd-canvas-footer div:last-child {
  border-right: 0;
}

.dsd-canvas-footer span,
.dsd-section-title span {
  color: #7b857f;
  font-size: 12px;
}

.dsd-canvas-footer strong {
  color: #17231f;
  font-size: 13px;
}

.dsd-panel {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 160px);
  overflow: hidden;
}

.dsd-panel__form {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  overflow: auto;
  padding: 16px;
}

.dsd-panel__section {
  border: 1px solid #edf0ed;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
}

.dsd-panel__section > strong,
.dsd-section-title strong {
  color: #17231f;
  font-size: 14px;
  font-weight: 900;
}

.dsd-section-title {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.dsd-asset-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.dsd-asset-card {
  background: #f6f7f6;
  border: 1px solid #edf0ed;
  border-radius: 8px;
  color: #17231f;
  cursor: pointer;
  min-height: 108px;
  overflow: hidden;
  padding: 8px;
  text-align: left;
}

.dsd-asset-card img {
  display: block;
  height: 66px;
  object-fit: cover;
  width: 100%;
}

.dsd-asset-card span {
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-top: 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dsd-panel__actions {
  border-top: 1px solid #edf0ed;
  justify-content: flex-end;
  padding: 12px;
}

.dsd-dialog {
  border-radius: 8px;
}

@media (max-width: 1180px) {
  .dsd-studio__body {
    grid-template-columns: 1fr;
  }

  .dsd-panel {
    max-height: none;
  }
}
</style>
